// components/dashboard/employer/applications/CandidateEvaluationCard.tsx
// #47b (Surface 1) — one-click AI evaluation of a candidate against the job they
// applied to. Advisory decision support: a match score + verdict + strengths /
// gaps / recommendation. Dark-launched — renders nothing unless the backend
// feature flag `cv_evaluation` is on. The result is cached server-side, so it
// re-opens instantly; "Utvärdera igen" re-runs (re-bills).
import React, { useEffect, useState } from 'react';
import { Sparkles, CheckCircle2, AlertTriangle, Loader2, RefreshCw, Info, MessageSquare } from 'lucide-react';
import { jobApplicationApi } from '@/lib/api/jobApplicationApi';
import { CandidateEvaluation, VerdictLabels } from '@/lib/models/candidateEvaluation';
import { ScoreBar, ScoreColor } from '@/components/custom-ui/score-bar';
import { useToast } from '@/hooks/use-toast';
import { useLinkLauncher } from '@/contexts/LinkLauncherContext';

interface Props {
    applicationId: string;
    applicantName?: string;
    roleLabel?: string; // annons role — makes the Link chip read "Namn – Roll" (this application)
    // Called after opening Link (so the parent modal can close and not obscure it).
    onOpenLink?: () => void;
}

const verdictColor = (verdict: string): ScoreColor =>
    verdict === 'strong' ? 'green' : verdict === 'weak' ? 'red' : 'orange';

const verdictBadgeClass = (verdict: string): string =>
    verdict === 'strong'
        ? 'bg-green-100 text-green-700'
        : verdict === 'weak'
        ? 'bg-red-100 text-red-700'
        : 'bg-orange-100 text-orange-700';

export const CandidateEvaluationCard: React.FC<Props> = ({ applicationId, applicantName, roleLabel, onOpenLink }) => {
    const { toast } = useToast();
    const { stageLink } = useLinkLauncher();
    const [enabled, setEnabled] = useState<boolean | null>(null); // null = still checking
    const [evaluation, setEvaluation] = useState<CandidateEvaluation | null>(null);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);

    // Check the feature flag first; only then load any cached evaluation.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const features = await jobApplicationApi.getFeatures();
            if (cancelled) return;
            setEnabled(features.cv_evaluation);
            if (!features.cv_evaluation) {
                setLoading(false);
                return;
            }
            try {
                const cached = await jobApplicationApi.getEvaluation(applicationId);
                if (!cancelled) setEvaluation(cached);
            } catch {
                /* no cached evaluation — leave null */
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [applicationId]);

    const runEvaluation = async () => {
        setRunning(true);
        try {
            const result = await jobApplicationApi.evaluateApplicant(applicationId);
            setEvaluation(result);
        } catch {
            toast({ title: 'Kunde inte utvärdera', description: 'Försök igen om en stund.', variant: 'destructive' });
        } finally {
            setRunning(false);
        }
    };

    // #47b (Surface 2) — open Link to discuss the candidate. We STAGE the prompt in
    // the composer (unsent) with the 'application' reference attached — on send the
    // backend injects the need + candidate profile + latest evaluation as context.
    // Staged (not auto-sent) deliberately: it opens a FRESH chat and mirrors the
    // normal manual-send path, avoiding the auto-send-on-open race and the
    // continue-or-new resume that could land on a stale/broken conversation.
    const askLinkAboutCandidate = () => {
        const who = applicantName || 'kandidaten';
        // Chip reads "Namn – Roll" so it's clearly THIS application (candidate + role),
        // not an ambiguous person reference.
        const label = roleLabel ? `${who} – ${roleLabel}` : who;
        stageLink({
            reference: { type: 'application', id: applicationId },
            label,
            prompt: 'Hjälp mig bedöma hur väl kandidaten matchar rollen — var kravförankrad och rådgivande.',
        });
        onOpenLink?.();
    };

    // Feature off (or still checking / off) → render nothing.
    if (enabled === null || enabled === false) return null;

    return (
        <div className="rounded-xl border border-[#1A4D4F]/20 bg-[#A4C3B2]/10 p-4">
            <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#1A4D4F]" />
                    <h3 className="text-sm font-semibold text-gray-900">Link-utvärdering</h3>
                </div>
                {evaluation && (
                    <button
                        type="button"
                        onClick={runEvaluation}
                        disabled={running}
                        className="inline-flex items-center gap-1 text-xs font-medium text-[#1A4D4F] hover:underline disabled:opacity-60"
                    >
                        {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        Utvärdera igen
                    </button>
                )}
            </div>

            {loading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Laddar…
                </div>
            ) : !evaluation ? (
                <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                        Låt Link bedöma hur väl kandidaten matchar jobbet — som stöd inför ditt beslut.
                    </p>
                    <button
                        type="button"
                        onClick={runEvaluation}
                        disabled={running}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#1A4D4F] text-white text-sm font-medium hover:bg-[#163f41] disabled:opacity-60"
                    >
                        {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {running ? 'Utvärderar…' : 'Utvärdera kandidat med Link'}
                    </button>
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${verdictBadgeClass(evaluation.verdict)}`}>
                            {VerdictLabels[evaluation.verdict] || evaluation.verdict}
                        </span>
                        <span className="text-xs text-gray-500">{evaluation.score}/100</span>
                    </div>

                    <ScoreBar
                        label="Matchning"
                        score={evaluation.score}
                        maxScore={100}
                        color={verdictColor(evaluation.verdict)}
                    />

                    {evaluation.summary && <p className="text-sm text-gray-700">{evaluation.summary}</p>}

                    {evaluation.strengths.length > 0 && (
                        <div>
                            <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Styrkor</p>
                            <ul className="space-y-1">
                                {evaluation.strengths.map((s, i) => (
                                    <li key={i} className="flex items-start gap-1.5 text-sm text-gray-700">
                                        <CheckCircle2 className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" />
                                        <span>{s}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {evaluation.gaps.length > 0 && (
                        <div>
                            <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Luckor</p>
                            <ul className="space-y-1">
                                {evaluation.gaps.map((g, i) => (
                                    <li key={i} className="flex items-start gap-1.5 text-sm text-gray-700">
                                        <AlertTriangle className="w-3.5 h-3.5 text-orange-500 mt-0.5 flex-shrink-0" />
                                        <span>{g}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {evaluation.recommendation && (
                        <div className="rounded-lg bg-white/70 border border-gray-100 p-2.5">
                            <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-0.5">Rekommendation</p>
                            <p className="text-sm font-medium text-gray-800">{evaluation.recommendation}</p>
                        </div>
                    )}

                    <div className="flex items-start gap-1.5 text-[11px] text-gray-400 pt-1">
                        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span>
                            Rådgivande stöd genererat av AI — inte ett automatiskt beslut. Väg alltid in din egen bedömning.
                            {evaluation.created_at && ` Analyserad ${new Date(evaluation.created_at).toLocaleDateString('sv-SE')}.`}
                        </span>
                    </div>
                </div>
            )}

            {!loading && (
                <button
                    type="button"
                    onClick={askLinkAboutCandidate}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-[#1A4D4F]/30 text-[#1A4D4F] text-sm font-medium hover:bg-[#1A4D4F]/5"
                >
                    <MessageSquare className="w-4 h-4" /> Fråga Link om kandidaten
                </button>
            )}
        </div>
    );
};

export default CandidateEvaluationCard;
